import { CustomModal } from '@amili-v3/amili-v3-design';
import styled from 'styled-components';

// Every violet in the auth design is the same hue at a different opacity, so
// derive the accent tones from one triplet rather than restating the rgb.
const ACCENT_RGB = '66, 46, 133';
const accent = (alpha: number) => `rgba(${ACCENT_RGB}, ${alpha})`;
const accentGradient = (from: number, to: number) =>
  `linear-gradient(135deg, ${accent(from)} 0%, ${accent(to)} 100%)`;
// The page tint has to be opaque: it covers the app's pink --background,
// which a low alpha would let through and shift the hue. So flatten the
// accent onto white here rather than relying on transparency.
const accentOnWhite = (alpha: number) =>
  `rgb(${ACCENT_RGB.split(', ')
    .map(c => Math.round(255 - alpha * (255 - Number(c))))
    .join(', ')})`;

const AUTH = {
  accent: accent(0.8),
  accentHover: accent(0.95),

  // The live auth app's #F9F9FB is the accent at ~3%, which reads as plain
  // white next to the card. This is the same hue carried a little further so
  // the page is visibly tinted without competing with the card on top of it.
  pageBg: accentOnWhite(0.045),
  text: '#0a0a0a',
  subtitle: 'rgba(10, 10, 10, 0.6)',
  helperText: 'rgba(0, 0, 0, 0.6)',
  placeholder: '#979797',
  // The toggle is a quiet grey in the design, not the accent.
  iconMuted: '#8a8a99',

  card: {
    top: 'rgba(247, 247, 245, 0.64)',
    bottom: 'rgba(230, 232, 235, 0.76)',
    border: '#d1d5db',
    borderHover: '#9ca3af',
  },

  field: {
    bg: 'rgba(255, 255, 255, 0.8)',
    border: 'rgba(0, 0, 0, 0.12)',
    focusBorder: accent(0.6),
  },

  // The submit button is a diagonal gradient of the accent rather than a flat
  // fill, and it deepens on hover instead of only shifting opacity.
  button: {
    bg: accentGradient(0.85, 0.75),
    bgHover: accentGradient(0.95, 0.85),
    border: accent(0.5),
    borderHover: accent(0.7),
    label: '#ffffff',
  },
};

export const AuthModal = styled(CustomModal)`
  --auth-accent: ${AUTH.accent};
  --auth-accent-hover: ${AUTH.accentHover};

  // The app paints a pink-tinted --background behind public routes; the auth
  // design wants a violet-tinted off-white. The modal's full-screen wrap
  // covers it.
  &.ant-modal::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -1;
    background: ${AUTH.pageBg};
  }

  // antd sizes the modal with a fixed width, which runs edge-to-edge on a
  // phone. Cap it so the card keeps a gutter on small screens.
  &.ant-modal {
    max-width: calc(100vw - 32px);
  }

  .ant-modal-content {
    // The card is the tinted surface and the fields sit on it in white, as in
    // the auth design — the inverse of the design system's default.
    position: relative;
    z-index: 10;
    backdrop-filter: blur(5px);
    background: linear-gradient(${AUTH.card.top} 0%, ${AUTH.card.bottom} 100%);
    border: 1px solid ${AUTH.card.border};
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transition: border-color 0.3s;

    &:hover {
      border-color: ${AUTH.card.borderHover};
    }
  }

  // The design's card is padding: 40px 30px 30px. antd splits the card into
  // header, body and footer, so the sides repeat on each and the 40px top and
  // 30px bottom land on the first and last.
  .ant-modal-header {
    padding: 40px 30px 0;
    border-bottom: none;
    // antd gives the header its own background; clear it so the whole card
    // reads as one tinted surface.
    background: transparent;

    @media screen and (max-width: 768px) {
      padding: 32px 24px 0;
    }
  }

  .ant-modal-title {
    padding: 0;
    line-height: 1;
  }

  .ant-modal-body {
    padding: 32px 30px 2px;

    @media screen and (max-width: 768px) {
      padding: 28px 24px 2px;
    }
  }

  // The label colour above is set loosely so it reaches every auth page; these
  // are the parts the design system still paints in its own palette. The
  // asterisk is rendered by ControlledInput rather than antd, so it carries
  // --primary (pink) until recoloured on its own element.
  .controlled-form-item.ant-form-item .ant-form-item-label {
    padding-bottom: 6px;

    > label {
      font-size: 12px;
      line-height: 16px;
    }

    .mark-required {
      color: var(--error);
    }

    .ant-form-item-tooltip {
      color: ${AUTH.iconMuted};
    }
  }

  // Without a footer there is nothing below the body to pad the card out.
  &.no-footer .ant-modal-body {
    padding-bottom: 36px;

    @media screen and (max-width: 768px) {
      padding-bottom: 28px;
    }
  }

  // The vertical layout reverses the footer so its buttons stack bottom-up.
  // These cards have a single submit button, and anything after it in the
  // footer array is a caption that belongs underneath, so undo the reversal.
  &.ant-modal .ant-modal-footer {
    flex-direction: column;
    gap: 0;
    padding: 0 30px 30px;

    @media screen and (max-width: 768px) {
      padding: 0 24px 28px;
    }
  }

  // The header padding above moves the card's top edge, so the back button
  // has to follow it rather than keep the dialog's own offsets.
  .prev-btn {
    top: 42px;
    inset-inline-start: 32px;
    color: var(--auth-accent);

    svg path {
      fill: var(--auth-accent);
    }

    @media screen and (max-width: 768px) {
      top: 34px;
      inset-inline-start: 20px;
    }
  }

  .controlled-form-item.ant-form-item {
    margin-bottom: 22px;

    .ant-form-item-explain {
      padding: 3px 2px 5px;
    }

    .ant-form-item-explain-error {
      font-size: 11px;
      line-height: 14px;
    }
  }

  .controlledInput {
    &.ant-input,
    &.ant-input-affix-wrapper {
      width: 100%;
      height: 43px;
      min-height: 43px;
      padding: 12px 14px;
      line-height: 17px;
      border: 1px solid ${AUTH.field.border};
      border-radius: 12px;
      background: ${AUTH.field.bg};
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.2s;

      font-size: 14px;
      font-weight: 400;
      color: ${AUTH.text};

      &::placeholder {
        font-size: 14px;
        font-weight: 400;
        color: ${AUTH.placeholder};
      }
    }

    // The password fields and the cleared/number fields are affix wrappers;
    // the inner input carries its own background and line height, so make both
    // follow the wrapper. Without the line height antd's 22px sits the text
    // low in the 17px content box. The class is .custom-input on password and
    // .custom-number-input on the confirmation code, and both are on the same
    // element as .controlledInput, so this matches the design system's own
    // selector shape to beat its min-height: 22px.
    &.custom-input.ant-input-affix-wrapper .ant-input,
    &.custom-number-input.ant-input-affix-wrapper .ant-input {
      background: transparent;
      height: 17px;
      min-height: 17px;
      line-height: 17px;

      // The inner input keeps the design system's own text and placeholder
      // colours, so restate them here to match the plain email field.
      color: ${AUTH.text};

      &::placeholder {
        color: ${AUTH.placeholder};
      }
    }

    &.ant-input:hover,
    &.ant-input-affix-wrapper:hover,
    &.ant-input:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${AUTH.field.focusBorder};
    }

    // The design marks focus with a border colour alone, so drop the design
    // system's focus ring rather than tint it. The error state clears its own
    // ring below, where it can outweigh the design system's error selector.
    &.ant-input:focus,
    &.ant-input-affix-wrapper.ant-input-affix-wrapper-focused {
      box-shadow: none;
    }
  }

  // The confirmation code field is the only one left with a clear button, so
  // give it the same quiet grey as the password eye.
  .controlledInput .ant-input-clear-icon {
    color: ${AUTH.iconMuted};
  }

  // The stroked eye passed in by authPasswordToggle inherits this colour. The
  // design system pins password icons to 15px wide for its own glyphs, which
  // squashes this one's square viewBox.
  .ant-input-password-icon {
    display: flex;
    color: ${AUTH.iconMuted};

    svg {
      width: 18px;
      height: 18px;
    }
  }

  // An invalid field keeps the same white fill and black value as a valid one
  // and is marked by the red border plus the message underneath. The design
  // system instead fills the field with its pink --input-error and recolours
  // the value, the placeholder and the suffix in red, so these restate the
  // resting colours over it. The selectors mirror the design system's own
  // shape, which the styled-component's class then outweighs.
  .controlled-form-item
    .ant-input-status-error:not(.ant-input-disabled):not(
      .ant-input-borderless
    ).ant-input,
  .controlled-form-item
    .ant-input-affix-wrapper-status-error:not(
      .ant-input-affix-wrapper-disabled
    ):not(.ant-input-affix-wrapper-borderless).ant-input-affix-wrapper {
    background: ${AUTH.field.bg};
    border-color: var(--error);
    color: ${AUTH.text};

    // The red border is the whole error state, so it stays put on hover and
    // focus rather than reverting to the accent.
    &:hover,
    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: var(--error);
    }

    // The design system rings the focused error field in --error-shadow; the
    // auth fields mark focus with the border alone.
    &:focus,
    &.ant-input-affix-wrapper-focused {
      box-shadow: none;
    }

    &::placeholder {
      color: ${AUTH.placeholder};
    }

    // The affix wrappers (password, confirmation code) carry the value in an
    // inner input, which the design system tints separately from the wrapper.
    .ant-input {
      background: transparent;
      color: ${AUTH.text};

      &::placeholder {
        color: ${AUTH.placeholder};
      }
    }

    .ant-input-suffix {
      background: transparent;
    }
  }

  .controlled-form-item .ant-form-item-control-input-content {
    min-height: 43px;
  }

  .custom-btn.ant-btn.ant-btn-lg {
    width: 100%;
    height: 43px;
    padding: 12px 14px;
    border: 1px solid ${AUTH.button.border};
    border-radius: 12px;
    background: ${AUTH.button.bg};
    margin-top: 6px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;

    font-size: 14px;
    font-weight: 600;
    color: ${AUTH.button.label};

    // antd's 22px line height overflows the 17px content box, which pushed
    // the label below centre. Centre it on the button instead.
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 17px;

    .ant-space,
    .ant-space-item {
      line-height: 17px;
    }

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible {
      background: ${AUTH.button.bgHover};
      border-color: ${AUTH.button.borderHover};
      color: ${AUTH.button.label};
    }
  }
`;

export const AuthLogoRow = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 36px;
`;

// The page name reads as a small tracked label under the logo rather than a
// dialog title, so the logo stays the card's primary mark.
export const AuthHeading = styled.div`
  // The logo svg carries slack below the wordmark, so this is tuned to the
  // gap the design shows between the lettering and the subtitle, not to the
  // svg's own box.
  margin-top: 3px;

  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.15em;
  text-align: center;
  text-transform: uppercase;
  text-wrap: balance;

  color: ${AUTH.subtitle};
`;

// Explanatory copy under the heading (e.g. "we'll email you a reset link").
export const AuthHelperText = styled.div`
  // The design says 1.5rem, but the app sets html { font-size: 25% }, so rem
  // here is 4px rather than 16px. Use the px the design intends.
  margin-bottom: 24px;

  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  text-wrap: balance;

  color: ${AUTH.helperText};
`;

// Footer line under the submit button, e.g. "Remember your password? Sign in".
export const AuthFooterRow = styled.div`
  margin-top: 20px;

  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;

  color: var(--dark-gray);
`;

export const AuthFooterLink = styled.a`
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  font-size: 12px;
  font-weight: 600;
  color: ${AUTH.accent};

  transition: color 0.2s;

  &:hover {
    color: ${AUTH.accentHover};
  }
`;
